import { Logo } from '@moniebook-web/components/molecules/logo';
import type { StepperProps } from '@moniebook-web/components/widgets/stepper';
import { Stepper } from '@moniebook-web/components/widgets/stepper';
import { ArrowLeftLine } from '@moniebook-web/icons-v2/arrows/arrow-left-line';
import { QuestionLine } from '@moniebook-web/icons-v2/system/question-line';
import { baseConfig } from '@moniebook-web/utils';
import { motion } from 'framer-motion';
import { type PropsWithChildren, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MobileStepper } from './mobile-stepper/mobile-stepper';

interface OnboardingLayoutProps {
    backLink?: string;
    backAction?: VoidFunction;
    progress?: number;
    actions?: ReactNode;
    stepper?: StepperProps;
    title?: string;
}

export function OnboardingLayout({
    backLink = '/',
    backAction,
    progress = 0,
    children,
    actions,
    stepper,
    title,
}: Readonly<PropsWithChildren<OnboardingLayoutProps>>) {
    const { MONIEBOOK_CUSTOMER_HELP_URL } = baseConfig;

    return (
        <section className='h-full overflow-auto'>
            <div className='relative mx-auto flex size-full max-w-1184 flex-col px-20'>
                <header className='sticky left-0 top-0 z-50 flex w-full flex-col bg-white'>
                    <div className='mx-auto h-60 w-full max-w-container px-20 md:px-0'>
                        <div className='mx-auto flex h-full max-w-1200 items-center justify-between'>
                            {backAction ? (
                                <button onClick={backAction}>
                                    <div className='rounded-full bg-neutral-s2 p-10 text-content-body-default'>
                                        <ArrowLeftLine />
                                    </div>
                                </button>
                            ) : (
                                <Link to={backLink}>
                                    {backLink === '/' ? (
                                        <Logo />
                                    ) : (
                                        <div className='rounded-full bg-neutral-s2 p-10 text-content-body-default'>
                                            <ArrowLeftLine />
                                        </div>
                                    )}
                                </Link>
                            )}

                            {title && (
                                <h1 className='font-general-sans text-18 font-semibold'>
                                    {title}
                                </h1>
                            )}

                            {actions || (
                                <div className='flex items-center gap-8 outline-none'>
                                    <div className='flex items-center text-feedback-neutral-outline-text'>
                                        <a
                                            href={MONIEBOOK_CUSTOMER_HELP_URL}
                                            target='_blank'
                                            rel='noreferrer'
                                            className='flex gap-4 px-7 text-center text-12 font-semibold transition-all hover:text-content-link-default hover:underline'>
                                            <QuestionLine />
                                            Get help
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {progress > 0 && (
                        <div className='h-4 w-full bg-feedback-neutral-ghost-default'>
                            <motion.div
                                className='h-4 bg-feedback-information-outline-text'
                                initial={{ width: 0 }}
                                animate={{
                                    width: `${progress}%`,
                                }}
                            />
                        </div>
                    )}

                    {stepper && (
                        <>
                            <div className='hidden sm:block'>
                                <Stepper {...stepper} />
                            </div>
                            <div className='fixed inset-x-0 bottom-0 bg-white px-16 py-8 sm:hidden'>
                                <MobileStepper {...stepper} />
                            </div>
                        </>
                    )}
                </header>
                {children}
            </div>
        </section>
    );
}

export function OnboardingLayoutContent({
    children,
}: Readonly<PropsWithChildren>) {
    return (
        <div className='px-16 desktop:px-24'>
            <div className='mx-auto h-full max-w-480 py-56'>{children}</div>
        </div>
    );
}
